/**
 * Completion Confirmation Dialog
 * Shown when technician clicks "Complete Check Only" in VHC
 * Requires completion reasons and/or notes before completing
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const COMPLETION_REASONS = [
  { code: 'no_show', label: "Customer didn't turn up" },
  { code: 'vhc_ok', label: 'VHC is all OK (no issues found)' },
  { code: 'customer_declined', label: 'Customer declined work' },
  { code: 'warranty_goodwill', label: 'Warranty/Goodwill job' },
  { code: 'other', label: 'Other (please specify in notes)' },
];

const CompletionConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  bookingInfo,
  loading = false,
}) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const handleReasonToggle = (code) => {
    setSelectedReasons((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
    setError('');
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    setError('');
  };

  const validate = () => {
    if (selectedReasons.length === 0 && !notes.trim()) {
      setError('Please select at least one reason or provide notes');
      return false;
    }
    if (selectedReasons.includes('other') && !notes.trim()) {
      setError('Please provide notes when selecting "Other"');
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onConfirm({
      completion_reasons: selectedReasons,
      completion_notes: notes,
    });
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setNotes('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">Complete Job</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {bookingInfo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{bookingInfo.customerName}</strong> -{' '}
              {bookingInfo.registration}
            </Typography>
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Select completion reason(s):
        </Typography>

        <FormGroup>
          {COMPLETION_REASONS.map((reason) => (
            <FormControlLabel
              key={reason.code}
              control={
                <Checkbox
                  checked={selectedReasons.includes(reason.code)}
                  onChange={() => handleReasonToggle(reason.code)}
                  disabled={loading}
                />
              }
              label={reason.label}
            />
          ))}
        </FormGroup>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Additional Notes"
          placeholder="Enter any additional details about the job completion..."
          value={notes}
          onChange={handleNotesChange}
          disabled={loading}
          sx={{ mt: 2 }}
          helperText={
            selectedReasons.includes('other')
              ? 'Notes required when "Other" is selected'
              : ''
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} /> : <CheckCircleIcon />
          }
        >
          {loading ? 'Completing...' : 'Complete Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletionConfirmationDialog;
